import { useState } from "react";
import { Award, Medal, Crown, Gem, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

export const MWP_TIERS = {
  bronze: { label: "Bronze", Icon: Award, need: "Any amount", bonus: 2, cls: "from-[#f3b37a] via-[#b8703a] to-[#7a3f1a] text-[#2a1205] shadow-[0_0_12px_rgba(205,127,50,.55)]" },
  silver: { label: "Silver", Icon: Medal, need: "250K+", bonus: 5, cls: "from-white via-slate-300 to-slate-500 text-slate-900 shadow-[0_0_12px_rgba(203,213,225,.6)]" },
  gold: { label: "Gold", Icon: Crown, need: "1M+", bonus: 10, cls: "from-[#fff3a8] via-[#f5c518] to-[#b8860b] text-[#3a2a00] shadow-[0_0_14px_rgba(250,204,21,.65)]" },
  platinum: { label: "Platinum", Icon: Gem, need: "10M+", bonus: 15, cls: "from-[#f0fbff] via-[#b9e6f5] to-[#7fa8c9] text-[#0b2333] shadow-[0_0_16px_rgba(165,243,252,.75)]" },
};

const FLOORS = [["silver", 250_000], ["gold", 1_000_000], ["platinum", 10_000_000]];
const short = (n) => (n >= 1e6 ? `${+(n / 1e6).toFixed(2)}M` : n >= 1e3 ? `${Math.ceil(n / 1e2) / 10}K` : `${Math.ceil(n)}`);

export const nextTierHint = (balance) => {
  if (!balance) return "Hold any amount of $MWP for Bronze";
  const nx = FLOORS.find(([, f]) => balance < f);
  return nx ? `Hold ${short(nx[1] - balance)} more for ${MWP_TIERS[nx[0]].label}` : "Top tier reached. You're Platinum!";
};

export const NextTier = ({ balance, testid }) => (
  <p data-testid={testid} className="text-xs text-cyan-200/90 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-cyan-300" />{nextTierHint(balance)}</p>
);

export const MwpBadge = ({ tier, big, testid }) => {
  const t = MWP_TIERS[tier];
  if (!t) return null;
  const { label, Icon, cls } = t;
  return big ? (
    <span data-testid={testid} title={`$MWP ${label} holder`} className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-gradient-to-br ${cls} font-bold text-xs uppercase tracking-[0.15em]`}>
      <Icon className="w-4 h-4" />$MWP {label}
    </span>
  ) : (
    <span data-testid={testid} title={`$MWP ${label} holder`} className={`shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-gradient-to-r ${cls} text-[9px] font-sans font-bold uppercase tracking-wider align-middle`}>
      <Icon className="w-2.5 h-2.5" /><span className="hidden sm:inline">{label}</span>
    </span>
  );
};

const fmt = (n) => (n >= 1e6 ? `${(n / 1e6).toFixed(n >= 1e7 ? 0 : 2)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}K` : n.toFixed(n < 1 ? 4 : 0));

export function MwpCard({ wallet, refresh }) {
  const [busy, setBusy] = useState(false);
  const m = wallet?.mwp;
  const recheck = async () => {
    setBusy(true);
    try { await api.post("/wallet/refresh"); await refresh(); toast.success("$MWP balance re-checked"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="mwp-card" className="mt-5 rounded-xl border border-slate-700/70 bg-[#061016]/80 p-4">
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-[11px] font-mono-game text-slate-400">$MWP HOLDER BADGE · BASE</p>
        {wallet && <button data-testid="mwp-refresh-btn" disabled={busy} onClick={recheck} title="Re-check $MWP" className="text-slate-400 hover:text-amber-200"><RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /></button>}
      </div>
      {!wallet ? <p className="text-xs text-slate-500">Link the wallet that holds your $MWP on NFT Boosts to earn a badge.</p>
        : m?.tier ? (
          <div className="space-y-2">
            <MwpBadge tier={m.tier} big testid="mwp-badge-profile" />
            <NextTier balance={m.balance} testid="mwp-next-tier" />
            <p className="text-xs text-slate-400"><span data-testid="mwp-balance" className="font-mono-game text-amber-200">{fmt(m.balance)}</span> $MWP · <span data-testid="mwp-bonus">+{MWP_TIERS[m.tier].bonus}% credits</span> from battles, quests and login</p>
          </div>
        ) : <p data-testid="mwp-none" className="text-xs text-slate-500">{m ? "No $MWP in this wallet yet." : "Checking your $MWP balance…"} Hold any amount on Base for Bronze.</p>}
      <div className="mt-3 grid grid-cols-4 gap-1.5">
        {Object.entries(MWP_TIERS).map(([k, t]) => (
          <div key={k} data-testid={`mwp-tier-${k}`} className={`rounded-lg px-1.5 py-1.5 text-center border ${m?.tier === k ? "border-amber-300/70 bg-amber-500/10" : "border-slate-800"}`}>
            <p className="text-[10px] text-slate-200">{t.label}</p><p className="text-[9px] font-mono-game text-slate-500">{t.need}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
